/*
 * File: ClinicalEditDialog.tsx
 * Project: mp-mobile-app
 *
 * Created by Cole on 04/12/2021
 *
 * Copyright (c) 2021 MacPractice, Inc. All rights reserved.
 *
 * Description: Displays the editable clinical widget list in a dialog.
 */
import React from "react";
import ClinicalEditList, { ClinicalEditListInner } from "./ClinicalEditList";
import UIDialog from "components/ui/UIDialog";
import Button from "@material-ui/core/Button";
import withStyles from "@material-ui/core/styles/withStyles";
import CloseIcon from "@material-ui/icons/Close";
import { CircularProgress, createStyles, Theme } from "@material-ui/core";
import { WithStyles } from "@material-ui/styles";
import { StoreContext } from "utils/useStore";
import { ContextType } from "react";

const progressSize = 24;

const styles = (theme: Theme) =>
  createStyles({
    save: {
      width: "100%",
      background: theme.palette.common.buttonGradient,
      color: theme.palette.common.white
    },
    saveWrapper: {
      width: "90%",
      position: "relative"
    },
    saveProgress: {
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -progressSize / 2,
      marginLeft: -progressSize / 2,
      color: theme.palette.common.white
    },
    contentWrapper: {
      position: "relative"
    },
    contentCover: {
      zIndex: 1000,
      backgroundColor: "black",
      opacity: 0.2,
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0
    }
  });

interface Props extends WithStyles<typeof styles> {
  onRef(instance?: ClinicalEditDialogInner): void;
}

interface State {
  saving: boolean;
  open: boolean;
}

export class ClinicalEditDialogInner extends React.Component<Props, State> {
  static contextType = StoreContext;
  context!: ContextType<typeof StoreContext>;

  state = { open: false, saving: false };
  editRef?: ClinicalEditListInner | undefined;

  componentDidMount() {
    this.props.onRef(this);
  }

  componentWillUnmount() {
    this.props.onRef(undefined);
  }

  save = async () => {
    this.setState(prev => ({ ...prev, saving: true }));
    await this.editRef?.save();
    this.toggleEditList();
    this.setState(prev => ({ ...prev, saving: false }));
  };

  get clinicalPreference() {
    if (this.context === undefined) {
      throw new Error("ClinicalEditDialog is missing context");
    }

    return this.context.getPreference("clinicalSummary");
  }

  rightNav = () => {
    return <CloseIcon onClick={this.toggleEditList} color="inherit" />;
  };

  toggleEditList = () => {
    this.setState({
      open: !this.state.open
    });
  };

  actions = () => {
    const { classes } = this.props;
    return (
      <div className={classes.saveWrapper}>
        <Button
          variant="contained"
          onClick={this.save}
          disabled={this.state.saving}
          className={classes.save}
        >
          Apply
        </Button>
        {this.state.saving && (
          // Show a spinner while the save is in progress
          <CircularProgress
            size={progressSize}
            className={classes.saveProgress}
          />
        )}
      </div>
    );
  };

  content = () => {
    return (
      <div className={this.props.classes.contentWrapper}>
        {this.state.saving && (
          // Block mouse events from reaching the list while we're saving.
          <div className={this.props.classes.contentCover} />
        )}
        <ClinicalEditList
          prefs={this.clinicalPreference.sortedPreferences}
          onRef={ref => {
            this.editRef = ref;
          }}
        />
      </div>
    );
  };

  render() {
    return (
      <UIDialog
        open={this.state.open}
        title="Edit Clinical Widgets"
        rightNav={this.rightNav}
        content={this.content}
        actions={this.actions}
      />
    );
  }
}

export default withStyles(styles)(ClinicalEditDialogInner);
